import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Select,
  MenuItem,
  Typography,
  InputLabel,
  FormControl,
  Box,
  TextareaAutosize,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";
import { showSnackbar } from "../../../redux/snackbar/actions";
import { encryptAES, decryptAES } from "../../../utils";

const useStyles = makeStyles({
  animatedButton: {
    background: "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
    color: "#fff",
    padding: "10px 30px",
    fontSize: "16px",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "5px",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
    },
  },
  dialogTitleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeButton: {
    marginLeft: "auto",
  },
});

const VendorCreation = ({ open, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      gst: "",
      mobilenumber1: "",
      mobilenumber2: "",
      email1: "",
      email2: "",
      notes: " ",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .min(3, "Name should be at least 3 characters")
        .max(25, "Name must be at most 25 characters"),
      address: Yup.string()
        .required("Address is required")
        .max(250, "Address must be at most 250 characters"),
      gst: Yup.string()
        .required("GST is required")
        .matches(
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
          "Invalid GST number"
        )
        .nullable(),
      mobilenumber1: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
        .required("Mobile number is required"),
      mobilenumber2: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
        .nullable(),
      email1: Yup.string()
        .required("Email1 is required")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Invalid email1 address"
        ),
      email2: Yup.string()
        .nullable()
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Invalid email2 address"
        ),

      notes: Yup.string()
        .nullable(),
    }),
    // onSubmit: handleSubmit,
  });
  const SourceOptions = ["SMS", "Whatsapp", "Email"];
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle disableTypography className={classes.dialogTitleContainer}>
        Add Vendor
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="name"
              label="Name"
              variant="outlined"
              fullWidth
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="gst"
              label="GST No"
              variant="outlined"
              fullWidth
              value={formik.values.gst}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.gst && Boolean(formik.errors.gst)}
              helperText={formik.touched.gst && formik.errors.gst}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="address"
              label="Address"
              variant="outlined"
              fullWidth
              multiline
              minRows={1}
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">City</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="City"
              >
                <MenuItem value="chennai">Chennai</MenuItem>
                <MenuItem value="madurai">Madurai</MenuItem>
                <MenuItem value="salem">Salem</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">State</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="State"
              >
                <MenuItem value="tamilnadu">Tamilnadu</MenuItem>
                <MenuItem value="kerala">Kerala</MenuItem>
                <MenuItem value="karanataka">Karanataka</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="mobilenumber1"
              label="Mobile Number 1"
              variant="outlined"
              fullWidth
              value={formik.values.mobilenumber1}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.mobilenumber1 &&
                Boolean(formik.errors.mobilenumber1)
              }
              helperText={
                formik.touched.mobilenumber1 && formik.errors.mobilenumber1
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="mobilenumber2"
              label="Mobile Number 2"
              variant="outlined"
              fullWidth
              value={formik.values.mobilenumber2}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.mobilenumber2 &&
                Boolean(formik.errors.mobilenumber2)
              }
              helperText={
                formik.touched.mobilenumber2 && formik.errors.mobilenumber2
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="email1"
              label="Email 1"
              variant="outlined"
              fullWidth
              value={formik.values.email1}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.email1 &&
                Boolean(formik.errors.email1)
              }
              helperText={
                formik.touched.email1 && formik.errors.email1
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="email2"
              label="Email 2"
              variant="outlined"
              //   disabled={mode === "edit"}
              fullWidth
              value={formik.values.email2}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.email2 &&
                Boolean(formik.errors.email2)
              }
              helperText={
                formik.touched.email2 && formik.errors.email2
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Autocomplete
              fullWidth
              multiple
              options={SourceOptions}
              value={formik.values.frequency}
              onChange={(e, value) => formik.setFieldValue("frequency", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sourced For"
                  name="frequency"
                  placeholder="Select Source"
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.frequency && Boolean(formik.errors.frequency)
                  }
                  helperText={
                    formik.touched.frequency ? formik.errors.frequency : " "
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="address"
              label="Notes"
              variant="outlined"
              fullWidth
              multiline
              minRows={1}
              value={formik.values.notes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.notes && Boolean(formik.errors.notes)}
              helperText={formik.touched.notes && formik.errors.notes}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box m={2}>
          <Button
            variant="contained"
            color="primary"
            className={classes.animatedButton}
            onClick={formik.handleSubmit}
          >
            Add Vendor
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default VendorCreation;
